import React from 'react';
import { motion } from 'framer-motion';
import { BanknoteIcon, WalletIcon, SparklesIcon, ClockIcon, CheckIcon } from 'lucide-react';
import { formatNaira } from '../../utils/formatCurrency';
export interface RefundOptionCardProps {
  type: 'bank_payout' | 'store_credit';
  amount: number;
  bonusAmount?: number;
  timeline: string;
  selected: boolean;
  onSelect: () => void;
  recommended?: boolean;
}
export function RefundOptionCard({
  type,
  amount,
  bonusAmount = 0,
  timeline,
  selected,
  onSelect,
  recommended = false
}: RefundOptionCardProps) {
  const isBankPayout = type === 'bank_payout';
  const totalAmount = amount + bonusAmount;
  return <motion.button onClick={onSelect} whileTap={{
    scale: 0.98
  }} className={`
        relative w-full text-left p-6 rounded-card border-2 transition-all duration-200
        ${selected ? isBankPayout ? 'border-primary-500 bg-primary-50' : 'border-accent-500 bg-accent-50' : 'border-surface-200 bg-white hover:border-surface-300'}
      `} data-id="element-156">
      {/* Recommended Badge */}
      {recommended && <div className="absolute -top-3 left-4 px-3 py-1 bg-accent-500 text-white text-caption font-semibold rounded-full flex items-center gap-1" data-id="element-157">
          <SparklesIcon className="w-3 h-3" data-id="element-158" />
          Recommended
        </div>}

      {/* Selection Indicator */}
      <div className="absolute top-4 right-4" data-id="element-159">
        <div className={`
            w-6 h-6 rounded-full border-2 flex items-center justify-center
            transition-colors duration-200
            ${selected ? isBankPayout ? 'border-primary-500 bg-primary-500' : 'border-accent-500 bg-accent-500' : 'border-surface-300 bg-white'}
          `} data-id="element-160">
          {selected && <CheckIcon className="w-4 h-4 text-white" data-id="element-161" />}
        </div>
      </div>

      {/* Icon */}
      <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center mb-4
          ${isBankPayout ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-600'}
        `} data-id="element-162">
        {isBankPayout ? <BanknoteIcon className="w-6 h-6" data-id="element-163" /> : <WalletIcon className="w-6 h-6" data-id="element-164" />}
      </div>

      {/* Title */}
      <h3 className="text-heading-md text-surface-900 mb-1" data-id="element-165">
        {isBankPayout ? 'Instant Bank Payout' : 'Store Credit'}
      </h3>

      {/* Amount */}
      <div className="mb-3" data-id="element-166">
        <span className="text-display-sm text-surface-900 tabular-nums" data-id="element-167">
          {formatNaira(totalAmount)}
        </span>
        {bonusAmount > 0 && <span className="ml-2 px-2 py-0.5 bg-accent-100 text-accent-700 text-body-sm font-semibold rounded-full" data-id="element-168">
            +{formatNaira(bonusAmount)} bonus
          </span>}
      </div>

      {/* Timeline */}
      <div className="flex items-center gap-2 text-body-sm text-surface-600" data-id="element-169">
        <ClockIcon className="w-4 h-4" data-id="element-170" />
        {timeline}
      </div>

      {/* Benefits for Store Credit */}
      {!isBankPayout && <div className="mt-4 pt-4 border-t border-accent-200" data-id="element-171">
          <p className="text-body-sm text-surface-700" data-id="element-172">
            <span className="font-medium text-accent-700" data-id="element-173">
              Rebook instantly
            </span>{' '}
            — Use your credit to book any flight, no waiting required.
          </p>
        </div>}
    </motion.button>;
}